import React from 'react'
import { useMutation } from '@tanstack/react-query'
import type { ShareBetData } from '@core/Areas/ShareBet/types'
import { isLoadBetSupportedWithShare } from '@core/Areas/ShareBet/ShareBetOutgoing/helpers'
import { PreviewDisplayFrame } from './Components/Common/PreviewDisplayFrame'
import { TicketShare } from './Components/TicketShare'
import { BetSlipShare } from './Components/BetSlipShare'
import { QuickbetShare } from './Components/QuickbetShare'
import { BetLoopShare } from './Components/BetLoopShare'
import { handleShareBetClick } from './helpers/handleShareBetClick'
import { handleShareBetLoopClick } from './helpers/handleShareBetLoopClick'

export const ShareWithExternal: React.FC<{ data: ShareBetData }> = ({ data }) => {
  const previewImageElRef = React.useRef<HTMLDivElement>(null)
  const [isCostShared, setShouldHideCost] = React.useState(false)

  const isBetLoopShare = data.contextType === 'betloop'
  const shouldAllowLink = shouldAllowLinkSharing(data) && isLoadBetSupportedWithShare(data)

  const { mutate, isError, isPending } = useMutation({
    mutationFn: () => {
      const previewEl = previewImageElRef.current
      return isBetLoopShare
        ? handleShareBetLoopClick({ data, previewEl })
        : handleShareBetClick({ data, previewEl, shouldAllowLink, isCostShared })
    },
  })

  const shouldEnableCostToggle = determineShouldEnableCostToggle(data)
  const handleToggleShouldShowCost = () => setShouldHideCost(curr => !curr)

  return (
    <PreviewDisplayFrame
      isError={isError}
      isLoading={isPending}
      handleShareClick={mutate}
      shouldShowCost={isCostShared}
      shareImageElRef={previewImageElRef}
      key={`force-rerender-${isCostShared}`}
      handleToggleShouldShowCost={shouldEnableCostToggle ? handleToggleShouldShowCost : undefined}
    >
      <>
        {data.contextType === 'ticket' && (
          <TicketShare share={data.share} isCostShared={isCostShared} />
        )}

        {data.contextType === 'betslip' && (
          <BetSlipShare share={data.share} isCostShared={isCostShared} />
        )}

        {data.contextType === 'quickbet' && (
          <QuickbetShare share={data.share} isCostShared={isCostShared} />
        )}

        {isBetLoopShare && <BetLoopShare share={data.share} />}
      </>
    </PreviewDisplayFrame>
  )
}

// =============
// Local Helpers
// =============

function shouldAllowLinkSharing(data: ShareBetData): boolean {
  if (data.contextType === 'betslip') return true
  if (data.contextType === 'quickbet') return true

  return data.contextType === 'ticket' && data.share.activeTab === 'pending'
}

function determineShouldEnableCostToggle(data: ShareBetData): boolean {
  if (data.contextType === 'betloop') return false
  if (data.contextType === 'betslip') return data.share.isBetPlaced

  if (data.contextType === 'quickbet') {
    const { win, place } = data.share.items[0].investment
    const hasValidInvestment = !!(win.value || place.value)
    return hasValidInvestment
  }

  return true
}
